import React, {
  forwardRef,
  KeyboardEventHandler,
  MouseEventHandler,
} from 'react'
import classNames from 'classnames'

import { DragLine } from './DragLine'
import { handleNavigationKeys } from '../helpers/handleNavigationKeys'

export const ROW_HEIGHT = 40
const STANDARD_INDENTATION = 24

export type RowLayoutHoverZone = 'top' | 'middle' | 'bottom'

export type RowLayoutRenderProps = {
  ref?: React.Ref<any>
  style: React.CSSProperties
  className: string
  tabIndex: number
  children: React.ReactElement
  onKeyDown: KeyboardEventHandler<HTMLElement>
  onClick?: (event: React.MouseEvent) => void
}

export type RowLayoutProps = {
  className?: string
  depth: number
  label: React.ReactNode
  icon: React.ReactNode
  isActive?: boolean
  hasActivity?: boolean
  sticky?: boolean
  leftPart?: React.ReactNode
  rightPart?: React.ReactNode
  innerRef?: React.RefObject<HTMLElement>
  isDragging?: boolean
  isDragOver?: boolean
  hoverZone?: RowLayoutHoverZone | null
  hidelabelOnHotjar?: boolean
  renderComponent?: (props: RowLayoutRenderProps) => React.ReactElement
  onClick?: MouseEventHandler
  onKeyDown?: KeyboardEventHandler
}

export const defaultRowLayoutRender = (props: RowLayoutRenderProps) => (
  <div {...props} />
)

export const RowLayout = forwardRef<any, RowLayoutProps>(
  (
    {
      className,
      depth,
      label,
      icon,
      isActive,
      hasActivity,
      sticky,
      leftPart,
      rightPart,
      isDragging,
      isDragOver,
      hoverZone,
      hidelabelOnHotjar,
      renderComponent = defaultRowLayoutRender,
      onClick,
      onKeyDown,
    },
    ref
  ) => {
    const dropIn = isDragOver && hoverZone === 'middle'
    const dropHoverStyle = dropIn ? { boxShadow: '0 0 0 2px #6B57F1' } : {}

    return renderComponent({
      ref,
      className: classNames(
        'flexbox flexbox-align-center position-relative radius-normal padding-right-small no-outline pointer',
        {
          'background-shade-50-on-hover': !isActive,
          'background-shade-50-on-focus': !isActive,
          'background-shade-45': isActive,
          'opacity-smaller': isDragging,
        },
        className
      ),
      style: {
        ...dropHoverStyle,
        marginLeft: depth > 0 ? STANDARD_INDENTATION * depth : 0,
        position: sticky ? 'sticky' : undefined,
        top: sticky ? 0 : undefined,
        zIndex: sticky ? 1 : undefined,
        marginBottom: 2,
        minHeight: 36,
      },
      onClick,
      onKeyDown: onKeyDown
        ? event => {
            handleNavigationKeys(event)
            onKeyDown(event)
          }
        : handleNavigationKeys,
      tabIndex: 0,
      children: (
        <>
          <div
            className="flexbox-no-shrink flexbox flexbox-centered margin-left-smaller"
            style={{ width: leftPart ? 20 : 4 }}
          >
            {leftPart}
          </div>
          <div
            className={classNames(
              'flexbox-no-shrink margin-right-small flexbox flexbox-centered',
              {
                'shade-00': hasActivity,
                'shade-20': !hasActivity,
              }
            )}
            style={{ width: 16, marginLeft: 2 }}
          >
            {icon}
          </div>
          <div
            className={classNames(
              'flexbox-grow shade-05 font-weight-medium font-size-large ellipsis',
              {
                'fs-block data-hj-suppress': hidelabelOnHotjar /* fs-block & data-hj-suppress hides content for FullStory & Hotjar */,
                'font-weight-bold full-white': hasActivity,
                'font-weight-medium shade-05': !hasActivity,
              }
            )}
            style={{
              lineHeight: '20px',
            }}
          >
            {label}
          </div>
          <div className="flexbox-no-shrink">{rightPart}</div>
          {isDragOver && hoverZone === 'top' && <DragLine position="top" />}
          {isDragOver &&
            hoverZone === 'bottom' && <DragLine position="bottom" />}
        </>
      ),
    })
  }
)
